import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { colors } from '@guryou/colors';
import { PaymentSettings, PricingModel } from '@guryou/core';
import { Spinner, ValidationSummary } from '@guryou/html-components';
import { createSubscriptionAsAnonymous, getSubscriptionPrice, stateIsNotReady, useEffectOnSuccess } from '@guryou/redux';
import { printDate, printPrice, printTime } from '@guryou/utils';
import Icon from '../../components/shared/Icon';
import { WidgetContext } from './WidgetContext';

// Validation schema
const subscriptionSchema = z.object({
  firstname: z.string().min(1, 'nameRequired'),
  lastname: z.string().min(1, 'surnameRequired'),
  email: z
    .string()
    .min(1, 'emailRequired')
    .email('emailRequired'),
  phone: z.string().min(1, 'phoneRequired'),
});

const WidgetSubscription = ({ gyClass, closeModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { subscriptionPrice } = useSelector(state => state.widget);
  const { selectedSessions, sessionActiveStep, setSessionActiveStep, error, acceptPayments, selsectedOfferForCourse, primaryColor } = useContext(
    WidgetContext
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use custom primary color if available, otherwise fallback to default mandy color
  const primary = primaryColor || 'var(--feel-primary)';
  const secondary = primaryColor || colors.cabaret;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(subscriptionSchema),
  });

  useEffect(() => {
    dispatch(getSubscriptionPrice({ classId: +selsectedOfferForCourse, sessions: selectedSessions }));
  }, [dispatch, selsectedOfferForCourse, selectedSessions]);

  useEffectOnSuccess([state => state.subscriptions.create], () => {
    setIsSubmitting(false);
    setSessionActiveStep(s => s - 1);
    closeModal();
  });

  if (stateIsNotReady(subscriptionPrice)) {
    return <Spinner />;
  }

  const onSubmit = data => {
    setIsSubmitting(true);
    dispatch(createSubscriptionAsAnonymous({ ...data, classId: +selsectedOfferForCourse, sessions: selectedSessions }));
  };

  let errorContent = null;
  if (error) {
    if (Array.isArray(error)) {
      errorContent = <ValidationSummary marginLeft={0} errorMessage={t(`VALIDATIONS.${error[0].message}`)} />;
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-8">
          {/* Sessions Section */}
          <div>
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: primary }}>
                <Icon name="user" size="sm" color="white" />
              </div>
              <div>
                <h3 className={`font-semibold text-[${colors.mineShaft}]`}>{t('BOOK_WIDGET.sessionsSelected')}</h3>
                <p className={`text-sm text-[${colors.boulder}]`}>{t('CENTERS_PAGE.seeSessions')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-white uppercase" style={{ background: `linear-gradient(to right, ${primary}, ${secondary})` }}>
                    <tr>
                      <th scope="col" className="px-6 py-4 font-semibold">
                        {t('CALENDAR.date')}
                      </th>
                      <th scope="col" className="px-6 py-4 font-semibold">
                        {t('SERVICES.startTime')}
                      </th>
                      <th scope="col" className="px-6 py-4 font-semibold">
                        {t('SERVICES.endTime')}
                      </th>
                      {gyClass.model !== PricingModel.total && (
                        <th scope="col" className="px-6 py-4 font-semibold">
                          {t('GENERAL.price')}
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {gyClass.sessions
                      .filter(session => selectedSessions.includes(session.id))
                      .map((x, index) => (
                        <tr
                          key={x.id}
                          className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                        >
                          <th scope="row" className={`px-6 py-4 font-medium text-[${colors.mineShaft}] whitespace-nowrap`}>
                            {printDate(x.startTime)}
                          </th>
                          <td className={`px-6 py-4 text-[${colors.boulder}]`}>{printTime(x.startTime)}</td>
                          <td className={`px-6 py-4 text-[${colors.boulder}]`}>{printTime(x.endTime)}</td>
                          {gyClass.model !== PricingModel.total && (
                            <td className="px-6 py-4 font-semibold" style={{ color: primary }}>
                              {printPrice(x.price)}
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Personal Data Section */}
          <div>
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ backgroundColor: primary }}>
                <Icon name="info" size="sm" color="white" />
              </div>
              <div>
                <h3 className={`font-semibold text-[${colors.mineShaft}]`}>{t('SERVICES.personalData')}</h3>
                <p className={`text-sm text-[${colors.boulder}]`}>{t('SERVICES.personalDataStepDescription')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium text-[${colors.mineShaft}] mb-2`}>{t('GENERAL.name')} *</label>
                <input
                  type="text"
                  {...register('firstname')}
                  style={{
                    borderColor: '#e5e7eb',
                    color: colors.mineShaft,
                  }}
                  className="w-full h-12 px-4 border rounded-lg focus:ring-2 transition-colors duration-200"
                  onFocus={e => {
                    e.currentTarget.style.borderColor = primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder={t('GENERAL.namePlaceholder')}
                />
                {errors.firstname && (
                  <p className="mt-1 text-sm" style={{ color: primary }}>
                    {t(`GENERAL.${errors.firstname.message}`)}
                  </p>
                )}
              </div>
              <div>
                <label className={`block text-sm font-medium text-[${colors.mineShaft}] mb-2`}>{t('GENERAL.surname')} *</label>
                <input
                  type="text"
                  {...register('lastname')}
                  style={{
                    borderColor: '#e5e7eb',
                    color: colors.mineShaft,
                  }}
                  className="w-full h-12 px-4 border rounded-lg focus:ring-2 transition-colors duration-200"
                  onFocus={e => {
                    e.currentTarget.style.borderColor = primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder={t('GENERAL.surnamePlaceholder')}
                />
                {errors.lastname && (
                  <p className="mt-1 text-sm" style={{ color: primary }}>
                    {t(`GENERAL.${errors.lastname.message}`)}
                  </p>
                )}
              </div>
              <div>
                <label className={`block text-sm font-medium text-[${colors.mineShaft}] mb-2`}>{t('GENERAL.email')} *</label>
                <input
                  type="email"
                  {...register('email')}
                  style={{
                    borderColor: '#e5e7eb',
                    color: colors.mineShaft,
                  }}
                  className="w-full h-12 px-4 border rounded-lg focus:ring-2 transition-colors duration-200"
                  onFocus={e => {
                    e.currentTarget.style.borderColor = primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder={t('GENERAL.emailPlaceholder')}
                />
                {errors.email && (
                  <p className="mt-1 text-sm" style={{ color: primary }}>
                    {t(`GENERAL.${errors.email.message}`)}
                  </p>
                )}
              </div>
              <div>
                <label className={`block text-sm font-medium text-[${colors.mineShaft}] mb-2`}>{t('GENERAL.phone')} *</label>
                <input
                  type="tel"
                  {...register('phone')}
                  style={{
                    borderColor: '#e5e7eb',
                    color: colors.mineShaft,
                  }}
                  className="w-full h-12 px-4 border rounded-lg focus:ring-2 transition-colors duration-200"
                  onFocus={e => {
                    e.currentTarget.style.borderColor = primary;
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${primary}40`;
                  }}
                  onBlur={e => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  placeholder={t('GENERAL.phonePlaceholder')}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm" style={{ color: primary }}>
                    {t(`GENERAL.${errors.phone.message}`)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 mt-6">
          {sessionActiveStep > 0 && (
            <button
              type="button"
              onClick={() => setSessionActiveStep(s => s - 1)}
              style={{
                backgroundColor: 'white',
                borderColor: primary,
                color: primary,
              }}
              className="flex-1 border-2 font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center"
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = primary;
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = primary;
              }}
            >
              <Icon name="arrow-left" size="sm" className="mr-2" />
              {t('GENERAL.goBack')}
            </button>
          )}
          {[PaymentSettings.NoPayments, PaymentSettings.Optional].includes(acceptPayments) && (
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: `linear-gradient(to right, ${primary}, ${secondary})`,
              }}
              className={`flex-1 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  {t('GENERAL.loading')}
                </>
              ) : (
                <>
                  <Icon name="book" size="sm" className="mr-2" />
                  {t('NEWSLETTER.subscribe')} {printPrice(subscriptionPrice.data.priceWithoutDiscount)}
                </>
              )}
            </button>
          )}
          {[PaymentSettings.OnlineOnly, PaymentSettings.Optional].includes(acceptPayments) && (
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: `linear-gradient(to right, ${primary}, ${secondary})`,
              }}
              className={`flex-1 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  {t('GENERAL.loading')}
                </>
              ) : (
                <>
                  <Icon name="shopping-cart" size="sm" className="mr-2" />
                  {t('GENERAL.payNow')} {printPrice(subscriptionPrice.data.price)}
                </>
              )}
            </button>
          )}
        </div>
        {errorContent && <div className="mt-4">{errorContent}</div>}
      </form>
    </div>
  );
};

export default WidgetSubscription;
